import React, {createContext, useCallback, useEffect, useState} from 'react';
import {getFeatureFlagStatus} from 'client/components/FeatureFlag/featureFlag.utils';

const THEME_STORAGE_KEY = 'albert-theme-preference';
const DARK_MODE_CLASS = 'dark';

export type Theme = 'light' | 'dark';

interface ThemeContextProps {
  theme: Theme;
  isDarkMode: boolean;
  toggleTheme: () => void;
  isFeatureEnabled: boolean;
  isLoading: boolean;
}

export const ThemeContext = createContext<ThemeContextProps>({
  theme: 'light',
  isDarkMode: false,
  toggleTheme: () => {},
  isFeatureEnabled: false,
  isLoading: true
});

export const ThemeConsumer = ThemeContext.Consumer;

interface ThemeProviderProps extends PropsWithChildrenRequired {}

export const ThemeProvider = ({children}: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>('light');
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check feature flag and initialize theme
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        const featureEnabled = await getFeatureFlagStatus('color_mode_dark');
        setIsFeatureEnabled(featureEnabled);

        if (featureEnabled) {
          // Get saved preference from localStorage, default to light

          const stored = localStorage.getItem(THEME_STORAGE_KEY);

          const savedTheme: Theme =
            stored && (['light', 'dark'] as const).includes(stored as Theme)
              ? (stored as Theme)
              : 'light';

          setTheme(savedTheme);

          // Apply theme to body
          let root = document.documentElement;

          if (root) {
            if (savedTheme === 'dark') {
              root.classList.add(DARK_MODE_CLASS);
            } else {
              root.classList.remove(DARK_MODE_CLASS);
            }
          } else {
            // ensures dark mode cannot leak when FF is off
            setTheme('light');
            document.documentElement?.classList.remove(DARK_MODE_CLASS);
          }
        }
      } catch (error) {
        console.error('Error initializing theme:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeTheme();
  }, []);

  const toggleTheme = useCallback(() => {
    if (!isFeatureEnabled) return;

    setTheme((prevTheme) => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';

      // Save to localStorage
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);

      // Apply to html
      let root = document.querySelector('html');

      if (root) {
        if (newTheme === 'dark') {
          root.classList.add(DARK_MODE_CLASS);
        } else {
          root.classList.remove(DARK_MODE_CLASS);
        }
      }

      return newTheme;
    });
  }, [isFeatureEnabled]);

  const value: ThemeContextProps = {
    theme,
    isDarkMode: theme === 'dark',
    toggleTheme,
    isFeatureEnabled,
    isLoading
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
